import React, { useState, useRef } from 'react';
import Wrapper from './Wrapper.jsx';

import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormHelperText from '@mui/material/FormHelperText';

const styles = {
	root: {
		paddingTop: 1,
		borderRadius: 1,
		border: '1px solid rgba(0,0,0,0.23)',
		overflow: 'hidden',
		width: '100%',
	},
	label: {
		marginTop: 2,
	},
	imgWrapper: {
		width: '100%',
		height: 300,
		backgroundColor: 'white',
		marginTop: 1,
		marginBottom: 1,
		padding: 1,
		'& > img': {
			width: '100%',
			height: '100%',
			objectFit: 'contain',
		},
		'&:hover': {
			cursor: 'pointer',
		},
	},
	canvasWrapper: {
		overflow: 'hidden',
	},
	canvas: {
		width: 1000,
		height: 300,
		touchAction: 'none',
		backgroundColor: 'white',
		marginBottom: -5,
	},
};

let isDrawing;


function SignatureField({ name, value, onChange, label, helperText, required, disabled }) {
	const [isDialogOpen, setDialogOpen] = useState(false);

	const handleChange = img => {
		const target = { name, value: img };
		onChange({ target });
		setDialogOpen(false);
	};

	return (
		<Wrapper>
			<FormControl sx={{ marginBottom: 2, ...styles.root }}>
				<InputLabel sx={styles.label} shrink>{label}{required ? '*' : ''}</InputLabel>
				<SignatureInput onChange={handleChange} onClose={() => setDialogOpen(false)} open={isDialogOpen} />

				<Box sx={{...styles.imgWrapper, pointerEvents: disabled ? 'none' : 'default'}} onClick={() => setDialogOpen(true)} >
					{value && <img src={value} alt="" />}
				</Box>
				<FormHelperText>{helperText}</FormHelperText>
			</FormControl>
		</Wrapper>
	);
}

function SignatureInput({ open, onClose, onChange }) {
	const [temp, setTemp] = useState();
	const canvasRef = useRef();
	const dialogRef = useRef();

	const handleClose = () => {
		onClose();
		isDrawing = false;
	};

	const init = () => {
		const canvas = canvasRef.current;
		canvas.width = 2000;
		canvas.height = 600;
	};

	const mousedown = e => {
		const ctx = canvasRef.current?.getContext('2d');
		let x = 0;
		let y = 0;
		if (e.touches?.length) {
			const { top, left } = getCoords(e.target);
			x = e.touches[0].pageX - left;
			y = e.touches[0].pageY - top;
		} else {
			x = e.nativeEvent.offsetX;
			y = e.nativeEvent.offsetY;
		}
		ctx.beginPath();
		ctx.moveTo(x * 2, y * 2);
		ctx.strokeStyle = '#000000';
		ctx.lineWidth = 5;
		isDrawing = true;
	};

	const mousemove = e => {
		if (!isDrawing) return;
		const ctx = canvasRef.current?.getContext('2d');
		let x = 0;
		let y = 0;
		if (e.touches?.length) {
			const { top, left } = getCoords(e.target);
			x = e.touches[0].pageX - left;
			y = e.touches[0].pageY - top;
		} else {
			x = e.nativeEvent.offsetX;
			y = e.nativeEvent.offsetY;
		}
		ctx.lineTo(x * 2, y * 2);
		ctx.stroke();
	};

	const mouseup = e => {
		isDrawing = false;
		const data = cropImageFromCanvas(canvasRef.current);
		setTemp(data);
	};

	return (
		<Dialog onClose={handleClose} open={open} TransitionProps={{ onEntered: init }} >
			<div style={styles.canvasWrapper} ref={dialogRef}>
				<canvas
					ref={canvasRef}
					style={styles.canvas}
					onMouseDown={mousedown}
					onMouseUp={mouseup}
					onMouseMove={mousemove}
					onTouchStart={mousedown}
					onTouchEnd={e => { e.preventDefault(); mouseup(e); }}
					onTouchMove={mousemove}
					onTouchCancel={mouseup}
				/>
			</div>
			<DialogActions>
				<Button autoFocus onClick={onClose} color="primary">
					Cancel
				</Button>
				<Button onClick={() => onChange(temp)} color="primary">
					Ok
				</Button>
			</DialogActions>
		</Dialog>

	);
}


function cropImageFromCanvas(canvas) {
	const ctx = canvas.getContext('2d');
	const w = canvas.width;
	const h = canvas.height;
	const pix = { x: [], y: [] };
	const imageData = ctx.getImageData(0, 0, w, h);

	for (let y = 0; y < h; y++) {
		for (let x = 0; x < w; x++) {
			let i = (y * w + x) * 4;
			if (imageData.data[i + 3] > 0) {
				pix.x.push(x);
				pix.y.push(y);
			}
		}
	}
	pix.x.sort(function (a, b) { return a - b; });
	pix.y.sort(function (a, b) { return a - b; });

	const n = pix.x.length - 1;
	const wn = 1 + pix.x[n] - pix.x[0];
	const hn = 1 + pix.y[n] - pix.y[0];
	if (!wn || !hn) return;
	const cut = ctx.getImageData(pix.x[0], pix.y[0], wn, hn);

	const temp = document.createElement('canvas');
	temp.width = wn;
	temp.height = hn;
	temp.getContext('2d').putImageData(cut, 0, 0);
	return temp.toDataURL();
}

function getCoords(elem) { // crossbrowser version
	const box = elem.getBoundingClientRect();

	const body = document.body;
	const docEl = document.documentElement;

	const scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop;
	const scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft;

	const clientTop = docEl.clientTop || body.clientTop || 0;
	const clientLeft = docEl.clientLeft || body.clientLeft || 0;

	const top = box.top + scrollTop - clientTop;
	const left = box.left + scrollLeft - clientLeft;

	return { top: Math.round(top), left: Math.round(left) };
}


SignatureField.type = 'Signature';
SignatureField.spec = [{
	name: 'Signature',
	key: 'Signature',
	title: 'Signature',
	icon: 'PenTool',
	category: 'input',
	translatable: ['label'],
	fields: [{
		type: 'TextField',
		label: 'Label',
		name: 'label',
	}, {
		type: 'Checkbox',
		label: 'Required?',
		name: 'required',
	}],
}];


export default SignatureField;